import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/PlanTrip.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { jsPDF } from "jspdf";
import { FiDownload } from "react-icons/fi";

export default function PlanTrip() {
  const [destination, setDestination] = useState("");
  const [days, setDays] = useState(1);
  const [interests, setInterests] = useState([]);
  const [itinerary, setItinerary] = useState([]);
  const [activeDay, setActiveDay] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [lang, setLang] = useState("en");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [listening, setListening] = useState(false);

  const navigate = useNavigate();
  const { loggedIn, fetchWithAuth, loading: authLoading } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const { t, i18n } = useTranslation();

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    if (!authLoading && !loggedIn) navigate("/login");
  }, [loggedIn, navigate, authLoading]);

  // ------------------ Voice Input ------------------
  const handleVoiceInput = () => {
    if (isMobile) {
      alert("Voice input is not supported on mobile.");
      return;
    }
    if (!("webkitSpeechRecognition" in window)) {
      alert("Your browser does not support voice input.");
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = lang === "en" ? "en-US" : "hi-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);

    recognition.onresult = (event) => {
      const voiceDestination = event.results[0][0].transcript;
      setDestination(voiceDestination);
    };

    recognition.onerror = (event) => {
      console.error(event.error);
      setListening(false);
    };

    recognition.start();
  };

  // ------------------ Parse itinerary ------------------
  const parseItinerary = (text) => {
    console.log(text);
    if (!text) return [];

    const dayHeaderRegex = /Day \d+:|दिन \d+:/g;
    const dayMatches = [...text.matchAll(dayHeaderRegex)];
    const daysArray = [];

    for (let i = 0; i < dayMatches.length; i++) {
      const startIndex = dayMatches[i].index;
      const endIndex = i + 1 < dayMatches.length ? dayMatches[i + 1].index : text.length;
      const dayText = text.slice(startIndex, endIndex).trim();

      const titleMatch = dayText.match(dayHeaderRegex);
      const dayTitle = titleMatch ? titleMatch[0].trim() : `Day ${i + 1}:`;

      let dayContent = dayText.replace(dayHeaderRegex, "").trim();

      const lines = dayContent
        .split(/\n/)
        .map(line => line.trim())
        .filter(line => line !== "");

      const activities = lines.map((line) => {
        if (!line) return null;

        // Ignore headings like **Tea Gardens**
        if (/^\*{2}.*\*{2}$/.test(line)) return null;

        // Updated regex for times like 7:30 AM, 1 PM, 0:30 AM
        const timeRegex = /^(\d{1,2}(:\d{2})?\s*(AM|PM|am|pm|बजे)?)/;
        const match = line.match(timeRegex);

        if (match) {
          const time = match[0].trim();
          let activity = line
            .replace(timeRegex, "")
            .replace(/^[:\-]\s*/, "")
            .replace(/\(\s*\)/g, "")
            .trim();
          if (!activity) activity = "Details not provided";

          return { time, activity, isSuggestion: false };
        } else {
          return { activity: line, isSuggestion: true };
        }
      }).filter(Boolean);

      daysArray.push({ day: dayTitle, activities });
    }

    return daysArray;
  };




  // ------------------ Generate Itinerary ------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetchWithAuth(`${API_URL}/generate-itinerary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          destination: destination.trim(),
          days: Number(days),
          interests,
          lang,
        }),
      });

      if (response.status === 401) {
        setError("Token expired. Please login again.");
        return;
      }

      const data = await response.json();
      if (!data.itinerary) {
        setError("Failed to generate itinerary. Try again.");
        return;
      }

      const structuredItinerary = parseItinerary(data.itinerary);
      setItinerary(structuredItinerary);
      setActiveDay(0);
    } catch (err) {
      console.error(err);
      setError("Failed to generate itinerary. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // ------------------ Save Trip ------------------
  const handleSaveTrip = async () => {
    if (!itinerary || itinerary.length === 0) {
      setError("Generate itinerary before saving.");
      return;
    }

    try {
      const payload = {
        destination: t(destination.trim()),
        days: Number(days),
        interests,
        tripData: itinerary,
        lang,
      };

      const response = await fetchWithAuth(`${API_URL}/save-trip`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        toast.success("🏖️ Trip saved successfully! You can see it in your profile.");
        setError("");
        setTimeout(() => setMessage(""), 5000);
      } else {
        setError(data.message || "Trip could not be saved.");
        setMessage("");
        setTimeout(() => setError(""), 5000);
      }
    } catch (err) {
      console.error(err);
      setError("Error saving trip.");
    }
  };

  // ------------------ Voice Reading ------------------
  const speakItinerary = (text) => {
    if (!text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);

    const voices = window.speechSynthesis.getVoices();
    const voice = voices.find((v) => (lang === "en" ? v.lang.includes("en") : v.lang.includes("hi"))) || voices[0];
    utterance.voice = voice;
    utterance.lang = lang === "en" ? "en-US" : "hi-IN";

    utterance.onend = () => setIsSpeaking(false);
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeech = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  // ------------------ Language & Interests ------------------
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLang(lng);
  };

  const interestOptions = [
    "spiritual", "nature", "adventure", "heritage", "food", "beaches", "mountains",
    "wildlife", "culture", "shopping", "wellness", "historical", "water-sports", "photography"
  ];
  const toggleInterest = (interest) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter((i) => i !== interest));
    } else {
      setInterests([...interests, interest]);
    }
  };
  const handleDownloadPDF = () => {
    if (!itinerary || itinerary.length === 0) {
      toast.error("Generate itinerary first!");
      return;
    }

    const doc = new jsPDF("p", "mm", "a4"); // Portrait, mm units, A4
    const pageHeight = doc.internal.pageSize.height;
    let y = 20; // Initial Y position
    const lineHeight = 7; // Space between lines

    doc.setFontSize(16);
    doc.text(`Trip to ${destination}`, 10, y);
    y += 10;

    itinerary.forEach((day) => {
      doc.setFontSize(14);
      if (y + 10 > pageHeight) {
        doc.addPage();
        y = 20;
      }
      doc.text(day.day, 10, y);
      y += 8;

      day.activities.forEach((act) => {
        doc.setFontSize(12);
        let text = act.time ? `${act.time} - ${act.activity}` : act.activity;

        // Split text if it is too long for page width
        const splitText = doc.splitTextToSize(text, 180); // 180 mm width
        splitText.forEach((line) => {
          if (y + lineHeight > pageHeight) {
            doc.addPage();
            y = 20;
          }
          doc.text(line, 12, y);
          y += lineHeight;
        });
        y += 2; // Extra spacing between activities
      });

      y += 5; // Extra spacing between days
    });

    doc.save(`${destination}_Trip.pdf`);
  };


  // ------------------ Render ------------------
  return (
    <div className="plan-trip-container">
      <Navbar />
      <div className="plan-trip">
        <h2>{t("Plan Your Soulful Yatra Trip")}</h2>
        {error && <p className="plan-trip-error">{error}</p>}
        {message && <p className="plan-trip-message">{message}</p>}

        {/* Language Selector */}
        <div className="lang-select">
          <label>{t("Choose Language:")}</label>
          <select value={lang} onChange={(e) => changeLanguage(e.target.value)}>
            <option value="en">English</option>
            <option value="hi">हिन्दी</option>
          </select>
        </div>

        {/* Trip Form */}
        <form className="plan-trip-form" onSubmit={handleSubmit}>
          <label>
            {t("Destination:")}
            <input
              type="text"
              value={destination}
              onChange={(e) => setDestination(t(e.target.value))}
              placeholder={t("Enter city or place")}
              required
            />
            {!isMobile && (
              <button type="button" onClick={handleVoiceInput}>
                {listening ? "🎙️ Listening..." : `🎤 ${t("Speak")}`}
              </button>
            )}
            {isMobile && <small>🎤 Voice input not supported on mobile</small>}
          </label>

          <label>
            {t("Number of Days:")}
            <input
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              min="1"
              required
            />
          </label>

          <fieldset>
            <legend>{t("Select Interests:")}</legend>
            {interestOptions.map((interest) => (
              <label key={interest}>
                <input
                  type="checkbox"
                  value={interest}
                  checked={interests.includes(interest)}
                  onChange={() => toggleInterest(interest)}
                />
                {t(interest)}
              </label>
            ))}
          </fieldset>

          <button type="submit">{t("Generate Itinerary")}</button>
        </form>

        {loading && (
          <div className="plan-trip-loading-container">
            <p>{t("Generating itinerary...")}</p>
            <div className="loading-destinations">
              <div className="loading-spinner"></div>
            </div>
          </div>
        )}

        {/* Itinerary Display */}
        {itinerary.length > 0 && (
          <div className="itinerary-section">
            <h2>✨ {t("Your Personalized Travel Plan")} ✨</h2>
            {/* Share as PDF */}
            {/* Download PDF Button */}
            <div className="download-button-container">
              <button
                onClick={handleDownloadPDF}
                className="download-btn"
              >
                <FiDownload size={24} color="white" />
              </button>
            </div>



            <div className="day-selector">
              {itinerary.map((day, idx) => (
                <button
                  key={idx}
                  className={`day-chip ${activeDay === idx ? "active" : ""}`}
                  onClick={() => setActiveDay(idx)}
                >
                  {lang === "en" ? "Day" : "दिन"} {idx + 1}
                </button>
              ))}
            </div>

            <div className="day-card">

              <h3>{itinerary[activeDay].day}</h3>
              <ul>
                {itinerary[activeDay].activities.map((act, aIdx) => (
                  <li key={aIdx} className="activity-item">
                    {!act.isSuggestion ? (
                      <>
                        <input
                          type="text"
                          value={act.time}
                          className={`time-input ${act.time.trim() === "" ? "invalid" : ""}`}
                          placeholder="Time"
                          onChange={(e) => {
                            const updatedItinerary = [...itinerary];
                            updatedItinerary[activeDay].activities[aIdx].time = e.target.value;
                            setItinerary(updatedItinerary);
                          }}
                        />
                        <input
                          type="text"
                          value={act.activity}
                          className={`activity-input ${act.activity.trim() === "" ? "invalid" : ""}`}
                          placeholder="Activity"
                          onChange={(e) => {
                            const updatedItinerary = [...itinerary];
                            updatedItinerary[activeDay].activities[aIdx].activity = e.target.value;
                            setItinerary(updatedItinerary);
                          }}
                        />
                        <button
                          type="button"
                          className="remove-activity-btn"
                          onClick={() => {
                            const updatedItinerary = [...itinerary];
                            updatedItinerary[activeDay].activities.splice(aIdx, 1);
                            setItinerary(updatedItinerary);
                          }}
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <span className="suggestion-text">{act.activity}</span>
                    )}
                  </li>
                ))}
              </ul>

              {/* Add new activity */}
              <button
                type="button"
                className="add-activity-btn"
                onClick={() => {
                  const updatedItinerary = [...itinerary];
                  updatedItinerary[activeDay].activities.push({
                    time: "",
                    activity: "",
                    isSuggestion: false
                  });
                  setItinerary(updatedItinerary);
                }}
              >
                + {t("Add Activity")}
              </button>
            </div>

            {/* Voice Controls */}
            <div className="voice-controls">
              {!isSpeaking ? (
                <button
                  className="listen-trip"
                  onClick={() =>
                    speakItinerary(
                      itinerary[activeDay].activities
                        .map((a) => `${a.time} - ${a.activity}`)
                        .join(". ")
                    )
                  }
                >
                  🔊 {t("Read Itinerary")}
                </button>
              ) : (
                <button className="listen-trip" onClick={stopSpeech}>
                  ⏹ {t("Stop Reading")}
                </button>
              )}
            </div>

            {/* Save Trip */}
            <div className="save-trip-container">
              <button onClick={handleSaveTrip} className="save-trip-button">
                {t("Save Trip")}
              </button>

            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
